import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { catchError, Observable, of, switchMap } from 'rxjs';
import {
  KpiEvaluationsResponse,
  ProductionKeyPerformanceIndicatorEvaluationWithData,
  CompanyProductKpiEvaluationsGroup,
  ProductionKeyPerformanceIndicatorEvaluation,
  getKpiEvaluationStyle,
  extractTooltipSummary,
} from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import { ApiService } from 'src/app/services/api.service';
import { KpiEvaluationPreviewDialogComponent } from 'src/app/components/main/employees/performance/my-metrics/kpi-evaluation-preview-dialog/kpi-evaluation-preview-dialog.component';
import { formatMonthKeyFromDate, normalizeMonthKey } from 'src/app/components/main/shared/month-navigation/month-navigation.component';

interface CompanyProductKpiCard {
  id: string;
  metricName: string;
  statusColor: string;
  tooltip: string;
  rawData: ProductionKeyPerformanceIndicatorEvaluationWithData;
}

@Component({
  selector: 'app-company-product-internal-metrics',
  templateUrl: './company-product-internal-metrics.component.html',
  styleUrl: './company-product-internal-metrics.component.css',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyProductInternalMetricsComponent {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly company_product_id = Number(
    this.activated_route.parent?.snapshot.paramMap.get('id') ??
      this.activated_route.parent?.snapshot.paramMap.get('company_product_id') ??
      0
  );
  private readonly current_month_key = formatMonthKeyFromDate(new Date());

  readonly month = input<string | null>(null);

  readonly selected_month = computed((): string => normalizeMonthKey(this.month()) ?? this.current_month_key);

  private readonly kpi_evaluations_response = toSignal(
    toObservable(this.selected_month).pipe(
      switchMap((month: string): Observable<KpiEvaluationsResponse> => {
        const params = new HttpParams().set('month', month);

        return this.api.getCompanyProductKpiEvaluations(this.company_product_id, params).pipe(
          catchError((): Observable<KpiEvaluationsResponse> => {
            return of<KpiEvaluationsResponse>({
              evaluations: [],
              company_product_groups: [],
              start_date: '',
              end_date: '',
            });
          })
        );
      })
    ),
    {
      initialValue: {
        evaluations: [],
        company_product_groups: [],
        start_date: '',
        end_date: '',
      } as KpiEvaluationsResponse,
    }
  );

  readonly company_product_name = computed((): string => {
    const group = this.selected_company_product_group();
    return group?.company_product.name ?? '';
  });

  readonly company_product_logo_url = computed((): string => {
    const group = this.selected_company_product_group();
    return group?.company_product.logo_url || '/img/default-company_product.png';
  });

  readonly company_product_cards = computed<CompanyProductKpiCard[]>(() => {
    const group = this.selected_company_product_group();
    if (!group) {
      return [];
    }

    return group.company_product_kpis.map((kpi_evaluation, index) => this.mapCompanyProductCard(kpi_evaluation, 'company_product', index));
  });

  readonly my_company_product_cards = computed<CompanyProductKpiCard[]>(() => {
    const group = this.selected_company_product_group();
    if (!group) {
      return [];
    }

    return group.my_company_product_kpis.map((kpi_evaluation, index) =>
      this.mapCompanyProductCard(kpi_evaluation, 'my-company_product', index)
    );
  });

  readonly has_company_product_metrics = computed((): boolean => {
    return this.company_product_cards().length > 0 || this.my_company_product_cards().length > 0;
  });

  openKpiCardPreviewDialog(kpi_data: ProductionKeyPerformanceIndicatorEvaluationWithData): void {
    this.dialog.open(KpiEvaluationPreviewDialogComponent, {
      data: kpi_data,
      panelClass: 'kpi-preview-dialog-panel',
      autoFocus: false,
    });
  }

  private selected_company_product_group(): CompanyProductKpiEvaluationsGroup | null {
    return (
      this.kpi_evaluations_response()?.company_product_groups?.find(group => group.company_product.id === this.company_product_id) ?? null
    );
  }

  private mapCompanyProductCard(
    kpi_evaluation: ProductionKeyPerformanceIndicatorEvaluationWithData,
    prefix: string,
    index: number
  ): CompanyProductKpiCard {
    const latest = this.getEvaluationAtSelectedMonth(kpi_evaluation);
    const style = getKpiEvaluationStyle(latest?.evaluation ?? ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable);
    const indicator = kpi_evaluation.production_key_performance_indicator;

    return {
      id: `${prefix}-${kpi_evaluation.company_product_id ?? 'none'}-${indicator?.id ?? index}`,
      metricName: indicator?.name ?? 'Métrica',
      statusColor: style.color,
      tooltip: this.buildKpiTooltip(kpi_evaluation),
      rawData: kpi_evaluation,
    };
  }

  private getEvaluationAtSelectedMonth(
    kpi_evaluation: ProductionKeyPerformanceIndicatorEvaluationWithData
  ): ProductionKeyPerformanceIndicatorEvaluation | null {
    return kpi_evaluation.data?.[0] ?? null;
  }

  private buildKpiTooltip(kpi_evaluation: ProductionKeyPerformanceIndicatorEvaluationWithData): string {
    const latest = this.getEvaluationAtSelectedMonth(kpi_evaluation);
    const style = getKpiEvaluationStyle(latest?.evaluation ?? ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable);

    if (kpi_evaluation.has_exception) {
      return 'Tienes una excepción activa en este periodo.';
    }

    if (!latest) {
      return 'Sin datos todavía.';
    }
    const backend_tooltip = extractTooltipSummary(latest)?.trim();
    if (backend_tooltip) {
      return `${style.label}: ${backend_tooltip}`;
    }

    if (latest.percentage === null) {
      return 'No calculable en este periodo.';
    }

    return `${Math.round(latest.percentage)}% (${style.label.toLowerCase()}).`;
  }
}
